import React from "react";
import { Link } from "react-router-dom";
import { FileText, Image as ImageIcon, Gauge, Code2, ArrowUpRight } from "lucide-react";

interface EcosystemItem {
  name: string;
  domain: string;
  url: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  isCurrent: boolean;
  colorClass: string;
  iconBgClass: string;
}

const ecosystemItems: EcosystemItem[] = [
  {
    name: "247DocTools",
    domain: "247DocTools.online",
    url: "https://247doctools.online",
    description: "Convert, edit, compress, merge, and manage your documents and PDFs online.",
    icon: FileText,
    isCurrent: false,
    colorClass: "text-rose-600 hover:text-rose-700 border-rose-100",
    iconBgClass: "bg-rose-50 text-rose-600",
  },
  {
    name: "247ImageTools",
    domain: "247ImageTools.online",
    url: "https://247imagetools.online",
    description: "Optimize, resize, convert, and enhance your images with simple online tools.",
    icon: ImageIcon,
    isCurrent: false,
    colorClass: "text-emerald-600 hover:text-emerald-700 border-emerald-100",
    iconBgClass: "bg-emerald-50 text-emerald-600",
  },
  {
    name: "247SpeedTestPro",
    domain: "247SpeedTestPro.online",
    url: "https://247speedtestpro.online",
    description: "Check your internet speed, latency, ping, and network performance instantly.",
    icon: Gauge,
    isCurrent: false,
    colorClass: "text-indigo-600 hover:text-indigo-700 border-indigo-100",
    iconBgClass: "bg-indigo-50 text-indigo-600",
  },
  {
    name: "247DevToolsHub",
    domain: "247DevToolsHub.online",
    url: "https://247devtoolshub.online",
    description: "Useful developer tools for coding, formatting, testing, and productivity.",
    icon: Code2,
    isCurrent: true,
    colorClass: "text-blue-600 hover:text-blue-700 border-blue-100",
    iconBgClass: "bg-blue-50 text-blue-600",
  },
];

interface EcosystemSectionProps {
  variant?: "full" | "compact";
}

export function EcosystemSection({ variant = "full" }: EcosystemSectionProps) {
  if (variant === "compact") {
    return (
      <section 
        className="bg-slate-50 border-t border-slate-200 py-8 px-6 w-full"
        aria-label="Ecosystem of Free Tools"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight">
                Explore Our Free Online Tools
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Discover more powerful free tools from our growing collection of online utilities. No installation required.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ecosystemItems.map((item) => {
              const IconComponent = item.icon;
              if (item.isCurrent) {
                return (
                  <div
                    key={item.name}
                    className="flex items-center gap-3 p-3 bg-white border border-blue-200 rounded-xl shadow-sm relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 bg-blue-500 text-[9px] text-white font-semibold px-2 py-0.5 rounded-bl-lg">
                      Active
                    </div>
                    <div className={`p-2 rounded-lg shrink-0 ${item.iconBgClass}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 pr-8">
                      <h3 className="font-bold text-xs text-slate-900 truncate">{item.name}</h3>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">{item.description}</p>
                    </div>
                  </div>
                );
              }

              return (
                <a
                  key={item.name}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition-all duration-200 group text-left"
                  aria-label={`Explore ${item.name} (opens in new tab)`}
                >
                  <div className={`p-2 rounded-lg shrink-0 ${item.iconBgClass} group-hover:scale-105 transition-transform`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <h3 className="font-bold text-xs text-slate-900 truncate group-hover:text-blue-600 transition-colors">
                        {item.name}
                      </h3>
                      <ArrowUpRight className="w-3 h-3 text-slate-400 group-hover:text-blue-600 transition-colors" />
                    </div>
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">{item.description}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Full variant (ideal for homepage bottom before footer)
  return (
    <section 
      className="border-t border-slate-200 py-16 px-6 bg-slate-50/50 w-full"
      aria-label="Connected Tool Network"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Explore Our Free Online Tools
          </h2>
          <p className="text-slate-500 text-sm md:text-base mt-3 leading-relaxed">
            Discover more powerful free tools from our growing collection of online utilities. No installation required, fast and easy to use.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ecosystemItems.map((item) => {
            const IconComponent = item.icon;
            
            if (item.isCurrent) {
              return (
                <div
                  key={item.name}
                  className="bg-white border-2 border-blue-500 rounded-2xl p-5 flex flex-col justify-between h-full relative shadow-sm"
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-bold tracking-wider px-3 py-0.5 rounded-full uppercase">
                    You Are Here
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2.5 rounded-xl ${item.iconBgClass}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 leading-tight">{item.name}</h3>
                        <span className="text-[11px] text-slate-400 font-mono">{item.domain}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed mb-6">
                      {item.description}
                    </p>
                  </div>
                  <Link
                    to="/"
                    className="w-full py-2 px-4 text-center text-xs font-semibold rounded-xl bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors inline-flex items-center justify-center gap-1.5"
                    aria-label={`Browse free developer tools on ${item.name}`}
                  >
                    Browse Dev Tools
                  </Link>
                </div>
              );
            }

            return (
              <div
                key={item.name}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col justify-between h-full hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
              >
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2.5 rounded-xl ${item.iconBgClass} group-hover:scale-105 transition-transform`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-tight">
                        {item.name}
                      </h3>
                      <span className="text-[11px] text-slate-400 font-mono">{item.domain}</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed mb-6">
                    {item.description}
                  </p>
                </div>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2 px-4 text-center text-xs font-semibold rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all inline-flex items-center justify-center gap-1.5 shadow-sm"
                  aria-label={`Explore free tools on ${item.name} (opens in a new tab)`}
                >
                  <span>Explore Tools</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-950 transition-colors" />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
